import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import {
	AppButton,
	IconButton,
	Label,
	Input,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
	DotMatrix,
	Pagination,
	CollapsibleCard,
	Modal,
	ModalHeader
} from '@clickguard/design-system'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import { CrmIcon } from './crm-marks'
import { ConnectionsSheet, ConnectionSection } from './connections-sheet'
import SearchSm from '@untitled-ui/icons-react/build/esm/SearchSm'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
import { Link } from 'react-router-dom'
import { GEmpty } from 'components/basic-blocks/g-empty'
import { createCrmApi, CrmConnection, CrmFormReference } from 'services/crm/api'
import { useHubSpotConnect } from 'services/crm/use-hubspot-connect'
import { errorMessage } from 'pages/workspace/asset/lead-forms/api'
import { NewCrmModal } from './new-crm-modal'
import { SortHeadCell, SortState, nextSortState } from './sort-head-cell'

const CrmSection = ({
	title,
	children
}: {
	title: string
	children: ReactNode
}) => (
	<CollapsibleCard
		title={
			<span className="text-base font-semibold text-[rgb(var(--cg-text-primary))]">
				{title}
			</span>
		}
		triggerAriaLabel={title}
		radius="xl"
		padding="regular"
		shadow="none"
		bordered
		className="!p-4"
		defaultExpanded
	>
		{children}
	</CollapsibleCard>
)

const CrmStatus = ({ status }: { status: CrmConnection['status'] }) => (
	<Label size="sm" color={status === 'ACTIVE' ? 'success' : 'error'}>
		{status === 'ACTIVE' ? 'Connected' : 'No access'}
	</Label>
)

export const CrmTab = () => {
	const account = useAccountStore((s) => s.account)
	return <CrmTabContent key={account?.id || ''} />
}

const CrmTabContent = () => {
	const account = useAccountStore((s) => s.account)
	const asset = useAssetStore((s) => s.asset)
	const user = useUserStore((s) => s.user)
	const userAccess = useAccountStore((s) => s.userAccess)
	const canWrite = ['OWNER', 'WRITE'].includes(userAccess(user?.id || '') || '')
	const api = useMemo(() => createCrmApi(account?.id || ''), [account?.id])
	const [rows, setRows] = useState<CrmConnection[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [modal, setModal] = useState(false)
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState<SortState>(null)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [selected, setSelected] = useState<CrmConnection | null>(null)
	const [forms, setForms] = useState<CrmFormReference[]>([])
	const [detailLoading, setDetailLoading] = useState(false)
	const [confirmation, setConfirmation] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const reload = useCallback(async () => {
		setLoading(true)
		setError('')
		try {
			const connections = (await api.list()).connections
			setRows(connections)
			setSelected((current) =>
				current ? connections.find((c) => c.id === current.id) || null : null
			)
		} catch (e) {
			setError(errorMessage(e))
		} finally {
			setLoading(false)
		}
	}, [api])
	useEffect(() => {
		let active = true
		setRows([])
		setSelected(null)
		setModal(false)
		setLoading(true)
		setError('')
		void api
			.list()
			.then((r) => {
				if (active) setRows(r.connections)
			})
			.catch((e) => {
				if (active) setError(errorMessage(e))
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [api])
	useEffect(() => {
		if (!selected) return
		let active = true
		setDetailLoading(true)
		setForms([])
		void api
			.detail(selected.id)
			.then((r) => {
				if (active) {
					setForms(r.forms)
					setSelected(r)
				}
			})
			.catch((e) => {
				if (active) setError(errorMessage(e))
			})
			.finally(() => {
				if (active) setDetailLoading(false)
			})
		return () => {
			active = false
		}
	}, [api, selected?.id])
	const oauth = useHubSpotConnect(api, (c) => {
		setSelected(c)
		void reload()
	})
	const filtered = rows
		.filter((r) =>
			`${r.name} HubSpot ${r.portal_id} ${r.credential_identity || ''}`
				.toLowerCase()
				.includes(search.trim().toLowerCase())
		)
		.sort((a, b) =>
			sort
				? (sort.key === 'status'
						? a.status.localeCompare(b.status)
						: a.name.localeCompare(b.name)) * (sort.dir === 'asc' ? 1 : -1)
				: 0
		)
	const pages = Math.max(1, Math.ceil(filtered.length / pageSize))
	const currentPage = Math.min(page, pages)
	const startDelete = async () => {
		if (!selected) return
		setDetailLoading(true)
		try {
			setForms((await api.impact(selected.id)).forms)
			setConfirmation(true)
		} catch (e) {
			setError(errorMessage(e))
		} finally {
			setDetailLoading(false)
		}
	}
	return (
		<div className="flex flex-col gap-3 pt-3">
			<div className="flex items-center justify-between gap-3">
				<div className="w-[160px] [&_span[aria-hidden]]:!pl-1.5">
					<Input
						state="default"
						size="sm"
						leadingIcon={<SearchSm className="h-3.5 w-3.5" />}
						style={{ paddingTop: 6, paddingBottom: 6 }}
						className="[&:not(:focus-within)]:!border-[rgb(var(--cg-border))]"
						placeholder="Search"
						aria-label="Search CRM connections"
						value={search}
						onChange={(e) => {
							setSearch(e.target.value)
							setPage(1)
						}}
					/>
				</div>
				<div className="flex gap-3">
					<AppButton
						variant="primary"
						size="sm"
						label="New CRM"
						disabled={!canWrite}
						onClick={() => setModal(true)}
					/>
				</div>
			</div>
			{(error || oauth.error) && (
				<p role="alert" className="text-sm text-red-600">
					{error || oauth.error}
				</p>
			)}
			<div className="overflow-x-auto rounded-t-lg">
				<Table>
					<TableHeader className="cg-bg-surface-2">
						<TableRow className="hover:!bg-transparent">
							<SortHeadCell
								label="Name"
								sortKey="name"
								sort={sort}
								onSort={(key) => setSort(nextSortState(sort, key))}
							/>
							<TableHead>Account ID</TableHead>
							<TableHead>Credential</TableHead>
							<SortHeadCell
								label="Status"
								sortKey="status"
								sort={sort}
								onSort={(key) => setSort(nextSortState(sort, key))}
							/>
							<TableHead>
								<span className="sr-only">Actions</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={5}>
									<div className="flex justify-center p-8">
										<DotMatrix label="Loading CRM connections" />
									</div>
								</TableCell>
							</TableRow>
						) : !filtered.length ? (
							<TableRow>
								<TableCell colSpan={5}>
									<GEmpty
										title={
											search
												? 'No matching connections'
												: 'No CRM connections yet'
										}
										description="Connect HubSpot to flag lead deliverability in your CRM."
									/>
								</TableCell>
							</TableRow>
						) : (
							filtered
								.slice((currentPage - 1) * pageSize, currentPage * pageSize)
								.map((r) => (
									<TableRow key={r.id}>
										<TableCell>
											<div className="flex items-center gap-3">
												<CrmIcon provider={r.provider} />
												<div className="min-w-0">
													<p className="text-base font-medium cg-text-text-primary">
														{r.name}
													</p>
													<p className="text-sm cg-text-text-secondary">
														HubSpot
													</p>
												</div>
											</div>
										</TableCell>
										<TableCell truncate={false}>
											<span className="whitespace-nowrap text-base cg-text-text-secondary">
												{r.portal_id}
											</span>
										</TableCell>
										<TableCell>
											<span className="block max-w-[260px] truncate text-base cg-text-text-secondary">
												{r.credential_identity || 'HubSpot'}
											</span>
										</TableCell>
										<TableCell>
											<CrmStatus status={r.status} />
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<IconButton
													size="sm"
													color="gray"
													aria-label={`CRM settings for ${r.name}`}
													icon={<Settings01 className="h-4 w-4" />}
													onClick={() => setSelected(r)}
												/>
											</div>
										</TableCell>
									</TableRow>
								))
						)}
					</TableBody>
				</Table>
			</div>
			{!!filtered.length && (
				<Pagination
					page={currentPage}
					totalPages={pages}
					onPageChange={setPage}
					pageSize={pageSize}
					pageSizeOptions={[10, 20, 50]}
					onPageSizeChange={(size) => {
						setPageSize(size)
						setPage(1)
					}}
				/>
			)}
			<NewCrmModal
				open={modal}
				onClose={() => setModal(false)}
				api={api}
				assetId={asset?.account_id === account?.id ? asset?.id : undefined}
				onConnected={() => void reload()}
			/>
			<ConnectionsSheet
				open={!!selected}
				onClose={() => setSelected(null)}
				title="CRM settings"
				closeOnEscape={!confirmation}
				footer={
					<div className="flex justify-end">
						<AppButton
							variant="secondary"
							size="sm"
							label="Close"
							onClick={() => setSelected(null)}
						/>
					</div>
				}
			>
				<div className="flex flex-col gap-2">
					{selected && (
						<>
							<ConnectionSection className="flex items-center gap-3">
								<CrmIcon provider={selected.provider} />
								<div className="min-w-0 flex-1">
									<p className="break-words text-base font-medium cg-text-text-primary">
										{selected.name}
									</p>
									<p className="text-sm cg-text-text-secondary">HubSpot</p>
								</div>
							</ConnectionSection>
							<CrmSection title="Overview">
								<dl className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-x-4 gap-y-3 [&>dt]:text-sm [&>dt]:text-[rgb(var(--cg-text-secondary))] [&>dd]:text-base [&>dd]:break-words [&>dd]:text-[rgb(var(--cg-text-primary))]">
									<dt>Name</dt>
									<dd>{selected.name}</dd>
									<dt>ID</dt>
									<dd>{selected.portal_id}</dd>
									<dt>Connected at</dt>
									<dd>
										{new Date(selected.created_at).toLocaleDateString('en-US', {
											month: 'short',
											day: 'numeric',
											year: 'numeric'
										})}
									</dd>
									<dt>Connected by</dt>
									<dd>{selected.connected_by_name || selected.connected_by}</dd>
									<dt>Credential</dt>
									<dd>{selected.credential_identity || 'HubSpot'}</dd>
								</dl>
								{selected.status === 'NEEDS_REAUTH' && (
									<p className="text-[13px] leading-5 text-[rgb(var(--cg-danger))]">
										HubSpot revoked the access. Reauthorize with the same
										credential, or sign in with a new one.
									</p>
								)}
								<div className="flex items-center justify-end gap-2">
									{oauth.busy ? (
										<DotMatrix label="Connecting HubSpot" />
									) : (
										<>
											<AppButton
												label="New credential"
												variant="secondary"
												disabled={!canWrite}
												onClick={() => void oauth.connect(selected.id)}
											/>
											{selected.status === 'NEEDS_REAUTH' && (
												<AppButton
													label="Reauthorize"
													disabled={!canWrite}
													onClick={() => void oauth.connect(selected.id)}
												/>
											)}
										</>
									)}
								</div>
							</CrmSection>
							<CrmSection title="Lead forms using this connection">
								{detailLoading ? (
									<DotMatrix label="Loading lead forms" />
								) : forms.length ? (
									<ul className="flex flex-col gap-3">
										{forms.map((f) => (
											<li
												key={f.id}
												className="border-b cg-border-border-default pb-3 last:border-0 last:pb-0"
											>
												<Link
													className="break-words text-base font-medium cg-text-text-primary hover:underline"
													to={`/workspace/${account?.id}/asset/${f.asset_id}/lead-forms`}
												>
													{f.form_name}
												</Link>
												{f.form_url && (
													<p className="break-words text-sm cg-text-text-secondary">
														{f.form_url.replace(/^https?:\/\//, '')}
													</p>
												)}
											</li>
										))}
									</ul>
								) : (
									<p className="text-sm">No forms use this connection.</p>
								)}
							</CrmSection>
							<ConnectionSection>
								<h3 className="mb-1 text-base font-semibold text-[rgb(var(--cg-text-primary))]">
									Danger zone
								</h3>
								<p className="mb-4 text-sm">
									This removes the CRM connection with ClickGuard's system. No
									data will be deleted or edited on your CRM.
								</p>
								<div className="flex items-center justify-between gap-6">
									<span className="text-base font-medium cg-text-text-primary">
										Delete CRM connection
									</span>
									<AppButton
										label="Delete"
										variant="destructive"
										disabled={!canWrite || detailLoading}
										onClick={() => void startDelete()}
									/>
								</div>
							</ConnectionSection>
						</>
					)}
				</div>
			</ConnectionsSheet>
			<Modal
				open={confirmation}
				onClose={() => {
					if (!deleting) setConfirmation(false)
				}}
				size="md"
			>
				<div className="flex flex-col gap-4 p-8">
					<ModalHeader
						title="Delete CRM connection?"
						onClose={() => {
							if (!deleting) setConfirmation(false)
						}}
					/>
					<p>
						This removes this connection from the following forms. Existing CRM
						data is unchanged.
					</p>
					<ul>
						{forms.map((f) => (
							<li key={f.id}>{f.form_name}</li>
						))}
					</ul>
					{deleting ? (
						<DotMatrix label="Deleting CRM connection" />
					) : (
						<AppButton
							label="Delete"
							onClick={async () => {
								if (!selected) return
								setDeleting(true)
								try {
									await api.remove(selected.id)
									setConfirmation(false)
									setSelected(null)
									await reload()
								} catch (e) {
									setError(errorMessage(e))
								} finally {
									setDeleting(false)
								}
							}}
						/>
					)}
				</div>
			</Modal>
		</div>
	)
}
